import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { PAGE_SEO, buildBreadcrumbs } from '../utils/seoConfig';
import './FAQ.css';

const FAQ_DATA = [
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'What is FreshFitLab?',
        a: 'FreshFitLab is a meal planning and fitness platform. On the food side, discover 500+ recipes, plan your weekly meals, and shop for ingredients with Instacart integration. On the fitness side, browse 2,000+ exercises, follow structured workout programs, track your training schedule, and connect with coaches.'
      },
      {
        q: 'Do I need an account to use FreshFitLab?',
        a: 'No account is required to browse! You can explore recipes, exercises, workouts, programs, and coach profiles without signing in. A free account unlocks personal features like saving recipes, building meal plans, scheduling workouts, and tracking your fitness progress.'
      },
      {
        q: 'Is FreshFitLab free to use?',
        a: 'Yes! FreshFitLab offers a generous free tier for both food and fitness. Browse recipes, create meal plans, shop with Instacart, browse exercises, view workout programs, and schedule workouts — all at no cost. Free accounts can save up to 3 personal recipes, 3 saved shopping lists, and 3 personal workouts. Paid plans unlock more storage, AI features, and additional capabilities.'
      },
      {
        q: 'How do I sign up?',
        a: 'Click the "Sign in with Google" button in the navigation bar. We use Google authentication for a quick, secure sign-up — no new passwords to remember!'
      }
    ]
  },
  {
    category: 'Fitness — Exercises, Workouts & Programs',
    questions: [
      {
        q: 'How many exercises does FreshFitLab have?',
        a: 'FreshFitLab features over 2,000 exercises spanning strength training, cardio, flexibility, plyometrics, Olympic lifting, and more. Each exercise includes detailed descriptions, target muscle groups, equipment needed, difficulty level, and instructional videos where available.'
      },
      {
        q: 'How do I browse exercises?',
        a: 'Switch to fitness mode and click "Exercises" in the navigation. You can search by name, filter by muscle group (chest, back, legs, shoulders, arms, core), equipment type (barbell, dumbbell, bodyweight, machine, cable), difficulty level, and category. Each exercise card shows key details at a glance.'
      },
      {
        q: 'What is a workout vs. a program?',
        a: 'A workout is a single training session — a collection of exercises with specified sets, reps, and rest periods. A program is a multi-week training plan that organizes workouts across days and weeks, with progression built in. Think of workouts as individual sessions and programs as your complete training roadmap.'
      },
      {
        q: 'Can I create my own workouts?',
        a: 'Yes! Logged-in users can create custom workouts using the Workout Creator. Pick exercises from the library, set your reps/sets/rest periods, organize them into blocks (warmup, main, cooldown), and save. You can also clone any public workout and customize it to your needs.'
      },
      {
        q: 'Can I create my own programs?',
        a: 'Yes! Use the Program Creator to build multi-week training programs. Assign workouts to specific days, set rest days, add progression notes, and configure program details like difficulty and target audience. You can also clone public programs as a starting point.'
      },
      {
        q: 'How do I schedule a workout?',
        a: 'From any workout or program page, use the "Add to Schedule" option to place it on a specific date in your weekly calendar. Visit "My Workouts" to see your schedule, track progress, and mark workouts as started, completed, or skipped. Coaches can also assign workouts directly to your schedule.'
      }
    ]
  },
  {
    category: 'Fitness — My Workouts & Tracking',
    questions: [
      {
        q: 'What is the My Workouts page?',
        a: 'My Workouts is your personal weekly training calendar. It shows all your scheduled workouts organized by day, with the ability to navigate between weeks, start workouts, mark them complete, skip them, or remove them. Below the schedule you\'ll find your recent workout history.'
      },
      {
        q: 'How do I track my workout progress?',
        a: 'Each scheduled workout has status tracking: Scheduled → In Progress → Completed (or Skipped). When you start a workout, click "Start" to mark it in progress, then "Complete" when you\'re done. Your completed sessions appear in the Recent Workouts history section with duration and exercises completed.'
      },
      {
        q: 'Can a coach schedule workouts for me?',
        a: 'Yes! When you\'re connected with a coach, they can assign workouts, programs, or individual exercises directly to your schedule. Coach-assigned items appear in your My Workouts calendar with a "Assigned by coach" badge so you can tell them apart from self-scheduled items.'
      }
    ]
  },
  {
    category: 'Coaches & Training',
    questions: [
      {
        q: 'How do I find a coach?',
        a: 'Switch to fitness mode and click "Coaches" in the navigation. Browse available coaches, view their profiles, specializations, and published content (programs, workouts, exercises). Each coach has a dedicated profile page with their bio, credentials, and all their published training content.'
      },
      {
        q: 'How do I connect with a coach?',
        a: 'Visit a coach\'s profile page and click "Connect with Coach" to send a connection request. Once connected, your coach can assign workouts to your schedule, track your progress, and provide personalized training guidance.'
      },
      {
        q: 'Are coach services free?',
        a: 'Coaches set their own pricing. Some offer free coaching, while others charge for premium programs or one-on-one training. Pricing is displayed on each coach\'s profile and program pages. Free connection is always available — paid features vary by coach.'
      }
    ]
  },
  {
    category: 'Browsing & Discovering Recipes',
    questions: [
      {
        q: 'How many recipes are available?',
        a: 'FreshFitLab features over 500 curated recipes spanning cuisines from around the world — Mediterranean, Asian, American, Indian, Mexican, and more. New recipes are added regularly by our community of food enthusiasts and creators.'
      },
      {
        q: 'Can I filter recipes by dietary preferences?',
        a: 'Absolutely! Use our filter panel to narrow down recipes by dietary needs (Vegan, Vegetarian, Gluten-Free, Keto, Low-Carb, Paleo, etc.), cuisine type, prep time, and more. Your filters are remembered for convenience.'
      },
      {
        q: 'What information is included with each recipe?',
        a: 'Every recipe includes a photo, description, prep/cook times, serving size, ingredients list with quantities, step-by-step instructions, nutritional information (calories, protein, carbs, fat, fiber), required equipment, and dietary tags.'
      }
    ]
  },
  {
    category: 'Shopping with Instacart',
    questions: [
      {
        q: 'How does the Instacart integration work?',
        a: 'When viewing any recipe, click "Add to list" to add ingredients to your shopping list. From the shopping list page, click "Shop with Instacart" to send all your ingredients to Instacart. You can also buy ingredients for a single recipe directly via the "More" menu on each recipe. Instacart handles the rest — including delivery to your door!'
      },
      {
        q: 'Do I need an Instacart account?',
        a: 'Yes, you\'ll need an Instacart account to complete your purchase. When you click "Shop with Instacart," you\'ll be redirected to Instacart where you can sign in or create an account, choose your preferred store, and complete checkout.'
      },
      {
        q: 'Can I shop for multiple recipes at once?',
        a: 'Yes! Use the Shopping List feature to add multiple recipes from the menu or your meal plan. The shopping list aggregates all ingredients, combines duplicates, and adjusts quantities based on servings. When you\'re ready, click "Shop with Instacart" to send everything to your Instacart cart.'
      },
      {
        q: 'Can I adjust ingredient quantities?',
        a: 'Yes! Before shopping, you can adjust the serving size and the ingredient quantities will scale automatically. You can also deselect specific ingredients you don\'t need (like salt, oil, or items you already have).'
      }
    ]
  },
  {
    category: 'Shopping List',
    questions: [
      {
        q: 'What is the Shopping List feature?',
        a: 'The Shopping List is a dedicated page where you can collect ingredients from multiple recipes. Add recipes from the menu or your meal plan, adjust servings, hide recipes you don\'t need yet, check off items as you shop, and send everything to Instacart with one click.'
      },
      {
        q: 'Can I save my shopping lists?',
        a: 'Yes! Click "Save" to save your current shopping list for later. Free users can save up to 3 lists, Standard users get 25, and Premium users get 100. Lists are pre-named with today\'s date for easy reference.'
      },
      {
        q: 'Can I add custom items to my list?',
        a: 'Absolutely! Below the aggregated ingredients, you\'ll find an "Add Custom Item" section where you can add any item with a name, quantity, and unit. Great for adding household items or ingredients not in a recipe.'
      }
    ]
  },
  {
    category: 'Meal Planning & Nutrition',
    questions: [
      {
        q: 'What is the Meal Plan feature?',
        a: 'The Meal Plan is your personal weekly calendar for organizing meals. Add recipes to specific days, view your week at a glance, select days and add all menus to your shopping list, and share your meal plan with family or friends. It\'s the easiest way to plan your week of cooking.'
      },
      {
        q: 'Do I need to be logged in to use Meal Planning?',
        a: 'Yes, meal planning requires a free account. This allows us to save your plans, remember your preferences, and let you access your meal plan from any device.'
      },
      {
        q: 'What is MealPlanify?',
        a: 'MealPlanify is our AI-powered meal plan generator. Tell it your preferences — dietary restrictions, allergies, cuisine preferences, days to plan, meals per day — and it will automatically create a personalized meal plan for you using recipes from our collection. It\'s like having a personal meal planner!'
      },
      {
        q: 'What is Nutrition Planning?',
        a: 'Nutrition Planning lets you set calorie and macro targets, and our system helps you build a meal plan that meets your goals. Free users get 1 nutrition plan, Standard gets 5, and Premium gets 50. Creators can build nutrition plans for their clients too.'
      },
      {
        q: 'Can I share my meal plan with others?',
        a: 'Yes! Click the "Share" button on your meal plan to generate a unique link. Anyone with the link can view your planned meals and shop for ingredients. Perfect for sharing with family members or roommates. You can disable sharing at any time.'
      }
    ]
  },
  {
    category: 'Personal Recipes',
    questions: [
      {
        q: 'Can I add my own recipes?',
        a: 'Yes! Logged-in users can create their own personal recipes. Go to "My Menus" and click "Add New Menu" to manually enter a recipe, or use our URL extractor to import recipes from popular cooking websites automatically.'
      },
      {
        q: 'How does the URL recipe extractor work?',
        a: 'Paste a URL from popular recipe websites (like AllRecipes, Food Network, etc.) and our system will automatically extract the recipe details including ingredients, instructions, and cooking times. Review the extracted info, make any edits, and save to your collection.'
      },
      {
        q: 'Can I import recipes from images?',
        a: 'Yes! Our AI-powered image extractor can read recipes from photos. Upload an image of a recipe (from a cookbook, magazine, or screenshot) and our system will extract the text and format it into a proper recipe. Great for digitizing your family\'s handwritten recipes!'
      },
      {
        q: 'Are my personal recipes private?',
        a: 'Yes, personal recipes are private by default. Only you can see and use recipes in your "My Menus" section. They won\'t appear in the public recipe browsing area unless you become a creator and choose to publish them.'
      },
      {
        q: 'Is there a limit to how many recipes I can save?',
        a: 'Free accounts can save up to 3 personal recipes. Standard ($5/month) unlocks 25 recipes, and Premium ($15/month) gives you 100.'
      }
    ]
  },
  {
    category: 'Creator & Coach Program',
    questions: [
      {
        q: 'What is the FreshFitLab Creator & Coach Program?',
        a: 'Our program lets food enthusiasts and fitness professionals share their expertise and earn money! Nutrition creators publish recipes and earn commission on Instacart orders. Fitness coaches build workout programs, create exercises, train clients, and collect payments directly via Stripe Connect.'
      },
      {
        q: 'How do I become a creator or coach?',
        a: 'Sign in with your Google account, navigate to the Creator Registration page, and choose your specialization: Nutrition Creator (publish recipes), Fitness Coach (build workouts and programs, train clients), or Both. Accept the terms and you\'re instantly approved — no waiting period!'
      },
      {
        q: 'What can fitness coaches do?',
        a: 'Fitness coaches get a dedicated Coach Dashboard with tools to: create and publish exercises, workouts, and programs; manage client relationships; assign workouts to clients\' schedules; track client progress; set up payment collection via Stripe Connect; and customize their public coach profile page.'
      },
      {
        q: 'What can nutrition creators do?',
        a: 'Nutrition creators can publish recipes to the public collection, build nutrition plans for clients, track recipe views and Instacart orders, and earn commission on orders placed through their published recipes. Instacart commission rates are tier-based: 2% on Free Creator, 3% on Standard Creator, and 4% on Pro Creator.'
      },
      {
        q: 'Do published recipes and workouts require approval?',
        a: 'Yes, to maintain quality, published content goes through a brief moderation review before appearing publicly. You can still view and use your own content immediately — it just won\'t be visible to other users until approved. Most content is reviewed within 24-48 hours.'
      },
      {
        q: 'How do coach payments work?',
        a: 'Coaches collect client payments directly via Stripe Connect. Set up your Stripe account from the Coach Dashboard Payments tab, and payments from clients go straight to your bank account. You can track charges and payouts from your dashboard.'
      },
      {
        q: 'How do I track my earnings?',
        a: 'Your dashboard shows real-time stats. Nutrition creators see recipe views, Instacart orders, and commission earnings. Fitness coaches see client counts, content published, and payment activity — all with monthly and all-time breakdowns.'
      }
    ]
  },
  {
    category: 'Pricing & Plans',
    questions: [
      {
        q: 'What do the paid plans include?',
        a: 'Personal plans focus on storage and organization: Free includes 3 personal menus, 3 saved workouts, 1 custom program, 1 nutrition plan, and 3 shopping lists. Standard ($5/month) increases those limits to 25 personal menus, 15 saved workouts, 5 custom programs, 5 nutrition plans, and 25 shopping lists. Premium ($15/month) is for power users with the highest personal limits across menus, workouts, programs, nutrition plans, and shopping lists.'
      },
      {
        q: 'Can I use fitness features on the free plan?',
        a: 'Absolutely! Free users can browse all 2,000+ exercises, view all public workouts and programs, browse coach profiles, save up to 3 personal workouts, and schedule workouts on their calendar. Paid plans unlock more storage and advanced features.'
      },
      {
        q: 'How much does it cost to become a coach?',
        a: 'The Creator / Coach program has three tiers: Free Creator / Coach at $0 with 2% Instacart commission, Standard Creator / Coach at $5/month with 3% commission, and Pro Creator / Coach at $15/month with 4% commission. Higher tiers also increase public menu limits, client capacity, nutrition plan capacity, analytics, and support.'
      }
    ]
  },
  {
    category: 'Business Plans',
    questions: [
      {
        q: 'What is a Business plan?',
        a: 'Business plans are designed for organizations like physical therapy clinics, nutrition practices, wellness centers, and fitness studios. They let you onboard employees who manage clients, assign exercises and nutrition plans, and centralize billing under one organization account.'
      },
      {
        q: 'How does employee and client management work?',
        a: 'The business owner registers the organization and invites employees by email. Employees get their own dashboard where they can invite and manage their clients. Clients can join via a QR code posted at your business, an invite link, an email invitation, or by entering a 6-character join code. The owner can see all employees and all clients across the organization.'
      },
      {
        q: 'What limits do my clients get on a Business plan?',
        a: 'Clients linked to your business automatically receive elevated limits based on your plan tier. Business Starter clients get Standard-tier limits (25 menus, 15 workouts, 5 programs, etc.). Business Professional and Enterprise clients get Premium-tier limits (500+ menus, 500+ workouts, 100+ programs, etc.) — all without needing their own personal subscription.'
      },
      {
        q: 'How does SSO work with a Business plan?',
        a: 'Enterprise-tier business plans include SSO (Single Sign-On) integration via SAML 2.0, compatible with identity providers like Okta, Azure AD, Google Workspace, and OneLogin. The business owner configures their IdP from the dashboard, and employees or clients can sign in through their corporate identity provider and be automatically linked to the organization.'
      },
      {
        q: 'Can I switch from an individual Coach plan to a Business plan?',
        a: 'Yes! If you are currently an individual coach or creator, you can register a business organization from the Business Plans section on the pricing page. Your existing coach features remain active alongside your business plan. The business plan adds organizational management, employee dashboards, and elevated client limits on top of existing functionality.'
      },
      {
        q: 'What are the Business plan tiers?',
        a: 'Business Starter ($29/month) supports up to 5 employees and 50 clients. Business Professional ($79/month) supports up to 25 employees and 250 clients with Premium-tier client limits and priority support. Business Enterprise ($199/month) offers unlimited employees and clients, SSO integration, and dedicated support.'
      }
    ]
  },
  {
    category: 'Account & Privacy',
    questions: [
      {
        q: 'How is my data used?',
        a: 'We use your data only to provide and improve FreshFitLab services. Your email is used for account identification. Your meal plans, workout schedules, and preferences are stored to provide a personalized experience. We never sell your data. See our Privacy Policy for full details.'
      },
      {
        q: 'Can I delete my account?',
        a: 'Yes! Visit the Account Deletion page at freshfitlab.io/delete-account. Sign in to your account and follow the steps. This will permanently remove all your personal data, saved recipes, workout data, meal plans, and shopping lists from our system.'
      },
      {
        q: 'What happens to my content if I cancel my creator status?',
        a: 'If you stop being a creator, your published content will be removed from public view, but you\'ll retain access to it in your personal account (subject to standard limits for free users).'
      }
    ]
  },
  {
    category: 'Technical & Support',
    questions: [
      {
        q: 'Which browsers are supported?',
        a: 'FreshFitLab works best on modern browsers including Chrome, Firefox, Safari, and Edge. We recommend keeping your browser updated for the best experience.'
      },
      {
        q: 'Is there a mobile app?',
        a: 'Yes! FreshFitLab is available as a native app on both iOS (App Store) and Android (Google Play). The apps include all the features of the web version — browse recipes, fitness content, meal planning, shopping lists with Instacart, and more.'
      },
      {
        q: 'Who do I contact for support?',
        a: 'Paid subscribers can access the Support Portal from the navigation bar to create and track support tickets. Free users can browse the FAQ or upgrade to a paid plan for direct support.'
      },
      {
        q: 'What is the Support Portal?',
        a: 'The Support Portal is available to all paying subscribers (Standard, Premium, and Pro plans). You can create support tickets across categories like billing, technical issues, account help, coaching, and nutrition questions. Track your tickets, add screenshots, and get responses directly from our team.'
      },
      {
        q: 'What priority levels can I set on support tickets?',
        a: 'Standard plan subscribers can set Low or Medium priority. Premium and Pro subscribers unlock High and Urgent priority levels for faster response on critical issues.'
      },
      {
        q: 'How quickly will I get a response to my support ticket?',
        a: 'We aim to respond to all tickets within 24-48 hours. High and Urgent priority tickets (available on Premium/Pro plans) are prioritized for faster response times.'
      },
      {
        q: 'Can I attach screenshots to support tickets?',
        a: 'Yes! When creating a ticket or replying to one, you can attach image screenshots (PNG, JPG, GIF, WebP) to help illustrate the issue you are experiencing.'
      }
    ]
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button className="faq-question" onClick={onClick}>
        <span>{question}</span>
        <svg 
          className="faq-chevron" 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>
      <div className="faq-answer">
        <p>{answer}</p>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isItemOpen = (categoryIndex, questionIndex) => {
    return openItems[`${categoryIndex}-${questionIndex}`] || false;
  };

  const seo = PAGE_SEO['/faq'];
  const faqJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_DATA.flatMap((category) =>
      category.questions.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
      }))
    ),
  }), []);

  return (
    <div className="faq-page">
      <SEO
        title={seo.title}
        description={seo.description}
        canonical="/faq"
        jsonLd={[
          faqJsonLd,
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'FAQ', url: '/faq' },
          ]),
        ]}
      />
      <div className="faq-header">
        <h1>Frequently Asked Questions</h1>
        <p>Everything you need to know about FreshFitLab</p>
      </div>

      <div className="faq-content">
        {FAQ_DATA.map((category, categoryIndex) => (
          <div className="faq-category" key={categoryIndex}>
            <h2 className="faq-category-title">{category.category}</h2>
            <div className="faq-items">
              {category.questions.map((item, questionIndex) => (
                <FAQItem
                  key={questionIndex}
                  question={item.q}
                  answer={item.a}
                  isOpen={isItemOpen(categoryIndex, questionIndex)}
                  onClick={() => toggleItem(categoryIndex, questionIndex)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="faq-cta">
        <h3>Ready to get started?</h3>
        <p>Join thousands using FreshFitLab for meal planning and fitness training.</p>
        <div className="faq-cta-buttons">
          <Link to="/menu" className="faq-cta-primary">Browse Recipes</Link>
          <Link to="/fitness" className="faq-cta-secondary">Explore Fitness</Link>
          <Link to="/support" className="faq-cta-secondary">Support Portal</Link>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
